import {
  type GridColDef,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { AgButton, AgCheckbox, AgInput, AgText } from 'src/components/ui';

type CustomizeTableProps = {
  closeMenu: () => void;
  disabledColumns?: Record<string, boolean>;
  controlledVisibility?: Record<string, boolean>;
  /** When provided, each column checkbox gets `data-testid` of `${prefix}-${field}`. */
  columnTestIdPrefix?: string;
  /** Test id for the "Enable/Disable All" button. */
  enableAllTestId?: string;
  /** Test id for the "Reset" button. */
  resetTestId?: string;
};

export default function CustomizeTable({
  closeMenu,
  disabledColumns,
  controlledVisibility,
  columnTestIdPrefix,
  enableAllTestId,
  resetTestId,
}: CustomizeTableProps) {
  const apiRef = useGridApiContext();
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const initialState = useRef(columnVisibilityModel);
  const [search, setSearch] = useState('');

  const isColumnLocked = useCallback(
    (col: GridColDef & { actually_hideable?: boolean }) =>
      col.hideable === false ||
      col.actually_hideable === false ||
      disabledColumns?.[col.field] === true ||
      controlledVisibility?.[col.field] !== undefined,
    [disabledColumns, controlledVisibility]
  );

  const columnsToShow = useMemo(
    () =>
      columns.filter((col) => {
        if (['actions', '__tree_data_group__'].includes(col.field)) {
          return false;
        }

        // Locked columns that are hidden are internal — don't show them
        if (isColumnLocked(col) && columnVisibilityModel[col.field] === false) {
          return false;
        }

        if (!search) return true;
        return col.headerName?.toLocaleLowerCase().includes(search.toLocaleLowerCase());
      }),
    [columns, search, isColumnLocked, columnVisibilityModel]
  );

  const toggleColumn = useCallback(
    (field: string, checked: boolean) => {
      apiRef.current?.setColumnVisibility(field, checked);
    },
    [apiRef]
  );

  const toggleableColumns = useMemo(
    () => columnsToShow.filter((col) => !isColumnLocked(col)),
    [columnsToShow, isColumnLocked]
  );

  const allEnabled = useMemo(
    () => toggleableColumns.every((col) => columnVisibilityModel[col.field] !== false),
    [columnVisibilityModel, toggleableColumns]
  );

  const toggleAll = useCallback(
    (checked: boolean) => {
      const newColumnVisibilityModel = { ...columnVisibilityModel };
      toggleableColumns.forEach((col) => {
        newColumnVisibilityModel[col.field] = checked;
      });
      apiRef.current?.setColumnVisibilityModel(newColumnVisibilityModel);
    },
    [apiRef, columnVisibilityModel, toggleableColumns]
  );

  const reset = useCallback(() => {
    apiRef.current?.setColumnVisibilityModel(initialState.current);
    closeMenu();
  }, [apiRef, closeMenu]);

  return (
    <div className="min-w-[256px] flex-1 flex flex-col overflow-hidden">
      <div className="p-2 pl-4 shrink-0 border-b border-neutral-5">
        <AgText size="12">Customize Table</AgText>
      </div>
      <div className="px-1.5 shrink-0 pt-2 pb-1">
        <AgInput
          placeholder="Search columns"
          aria-label="Search columns"
          value={search}
          onChange={setSearch}
          type="search"
          size="sm"
          startContent={<RiSearchLine size={16} />}
          endContent={
            search && (
              <AgButton
                variant="ghost"
                size="icon-xs"
                shape="circle"
                aria-label="Clear column search"
                onPress={() => setSearch('')}
              >
                <RiCloseLine size={16} />
              </AgButton>
            )
          }
        />
      </div>
      <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
        {columnsToShow.map((col) => {
          const locked = isColumnLocked(col);
          return (
            <AgCheckbox
              key={col.field}
              isSelected={locked || columnVisibilityModel[col.field] !== false}
              isDisabled={locked}
              onChange={(val) => toggleColumn(col.field, val)}
              label={col.headerName ?? col.field}
              size="sm"
              className="px-2 py-1.5 hover:bg-neutral-2"
              data-testid={columnTestIdPrefix ? `${columnTestIdPrefix}-${col.field}` : undefined}
            />
          );
        })}
      </div>
      <div className="flex justify-between p-3 border-t border-neutral-5 shrink-0">
        <AgButton variant="ghost" shape="rounded" onPress={reset} data-testid={resetTestId}>
          Reset
        </AgButton>
        <AgButton
          variant="primary"
          shape="rounded"
          className="px-3"
          onPress={() => toggleAll(allEnabled ? false : true)}
          data-testid={enableAllTestId}
        >
          {allEnabled ? 'Disable' : 'Enable'} All
        </AgButton>
      </div>
    </div>
  );
}
